import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
// Minimal coordinate presence
const CoordinateGrid = () => <div className="absolute inset-0 w-full h-full opacity-[0.015] pointer-events-none" style={{
  backgroundImage: 'linear-gradient(#F5F5F5 0.5px, transparent 0.5px), linear-gradient(90deg, #F5F5F5 0.5px, transparent 0.5px)',
  backgroundSize: '80px 80px'
}} aria-hidden="true" data-id="element-352" />;
// Mock data structure for past scans
interface Scan {
  id: string;
  type: 'self' | 'work' | 'relationship' | 'child';
  name: string;
  names?: {
    person1: string;
    person2: string;
  }; // for relationship
  dateGenerated: string;
  status: 'full';
}
export function Archive() {
  const [scans, setScans] = useState<Scan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    // Simulate loading past scans from storage/API
    // In production, this would fetch from your backend
    const loadScans = () => {
      // Mock data - replace with actual data fetching
      const mockScans: Scan[] = [
        // {
        //   id: 'scan-001',
        //   type: 'self',
        //   name: 'John Doe',
        //   dateGenerated: '2024-01-15',
        //   status: 'full',
        // },
        // {
        //   id: 'scan-002',
        //   type: 'relationship',
        //   names: { person1: 'John Doe', person2: 'Jane Smith' },
        //   name: 'John Doe & Jane Smith',
        //   dateGenerated: '2024-01-10',
        //   status: 'full',
        // },
      ];
      setScans(mockScans);
      setIsLoading(false);
    };
    setTimeout(loadScans, 300); // Simulate loading delay
  }, []);
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'self':
        return 'Self System';
      case 'work':
        return 'Work System';
      case 'relationship':
        return 'Relationship';
      case 'child':
        return 'Child System';
      default:
        return 'Diagnostic';
    }
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'self':
        return '🧬';
      case 'work':
        return '💼';
      case 'relationship':
        return '💞';
      case 'child':
        return '🧒';
      default:
        return '📄';
    }
  };
  const getReportPath = (scan: Scan) => {
    switch (scan.type) {
      case 'work':
        return `/report/work/${scan.id}`;
      case 'relationship':
        return `/report/relationship/${scan.id}`;
      case 'child':
        return `/report/child/${scan.id}`;
      default:
        return `/report/${scan.id}`;
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  return <>
      <Helmet data-id="element-353">
        <title data-id="element-354">Archive | Decoder</title>
        <meta name="robots" content="noindex, nofollow" data-id="element-355" />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-356">
        {/* Navigation */}
        <header data-id="element-357">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-358">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-359">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-360">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-361" />
                <span className="text-base font-mono tracking-tight text-gray-400" data-id="element-362">
                  Lazy Monk Labs
                </span>
              </Link>
              <div className="flex items-center gap-8" data-id="element-363">
                <Link to="/decode" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-364">
                  Decoder
                </Link>
                <Link to="/careers" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-365">
                  Careers
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-500 hover:text-[#F5F5F5] transition-colors font-mono" data-id="element-366">
                  Contact
                </a>
              </div>
            </div>
          </nav>
        </header>

        <main className="pt-32 pb-16 px-6" data-id="element-367">
          <div className="max-w-4xl mx-auto" data-id="element-368">
            {/* 1. HEADER */}
            <div className="mb-16 space-y-4" data-id="element-369">
              <h1 className="text-4xl md:text-5xl font-serif leading-tight text-[#F5F5F5]" style={{
              fontFamily: 'Georgia, serif'
            }} data-id="element-370">
                Your Archive
              </h1>
              <p className="text-base text-gray-500" data-id="element-371">
                Previously unlocked scans.
              </p>
            </div>

            {/* 2. LIST OF SCANS or EMPTY STATE */}
            {isLoading ? <div className="py-20 text-center" data-id="element-372">
                <p className="text-sm text-gray-600 font-mono" data-id="element-373">Loading...</p>
              </div> : scans.length === 0 /* 3. EMPTY STATE */ ? <div className="py-20 text-center space-y-8" data-id="element-374">
                <div className="space-y-4" data-id="element-375">
                  <p className="text-lg text-gray-400" data-id="element-376">
                    Your archive is empty.
                  </p>
                  <p className="text-base text-gray-500" data-id="element-377">
                    Run a scan to generate your first diagnostic.
                  </p>
                </div>
                <Link to="/reports" className="inline-flex items-center gap-3 text-base text-[#F5F5F5] hover:text-gray-400 transition-colors group" data-id="element-378">
                  <span data-id="element-379">Go to Reports Menu</span>
                  <span className="text-gray-600 group-hover:text-gray-400 transition-colors" data-id="element-380">
                    →
                  </span>
                </Link>
              </div> /* LIST OF SCANS */ : <div className="space-y-4" data-id="element-381">
                {scans.map(scan => <Link key={scan.id} to={getReportPath(scan)} className="block group" data-id="element-382">
                    <div className="p-6 border border-[#2B2B2B] bg-[#0A0A0A] hover:border-[#64748B] transition-colors duration-300" data-id="element-383">
                      <div className="flex items-start justify-between gap-4" data-id="element-384">
                        <div className="flex items-start gap-4 flex-1" data-id="element-385">
                          <span className="text-2xl" data-id="element-386">
                            {getTypeIcon(scan.type)}
                          </span>
                          <div className="space-y-2 flex-1" data-id="element-387">
                            <div className="flex items-center gap-3" data-id="element-388">
                              <p className="text-xs font-mono text-gray-600 uppercase tracking-wider" data-id="element-389">
                                {getTypeLabel(scan.type)}
                              </p>
                              <span className="text-xs text-gray-700" data-id="element-390">•</span>
                              <p className="text-xs font-mono text-gray-600" data-id="element-391">
                                {formatDate(scan.dateGenerated)}
                              </p>
                            </div>
                            <p className="text-base text-[#F5F5F5]" data-id="element-392">
                              {scan.name}
                            </p>
                            <p className="text-xs text-gray-600 font-mono uppercase tracking-wider" data-id="element-393">
                              Status: Full
                            </p>
                          </div>
                        </div>
                        <span className="text-gray-600 group-hover:text-gray-400 transition-colors text-sm font-mono" data-id="element-394">
                          →
                        </span>
                      </div>
                    </div>
                  </Link>)}
              </div>}
          </div>
        </main>

        {/* Footer */}
        <footer className="py-12 px-6 border-t border-[#2B2B2B]" data-id="element-395">
          <div className="max-w-6xl mx-auto" data-id="element-396">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-8" data-id="element-397">
              <div data-id="element-398">
                <p className="text-sm text-gray-500 font-mono" data-id="element-399">
                  Lazy Monk Labs
                </p>
                <p className="text-xs text-gray-600 mt-1" data-id="element-400">
                  Pattern recognition systems
                </p>
              </div>

              <div className="flex gap-8 text-sm text-gray-500" data-id="element-401">
                <Link to="/decode" className="hover:text-[#F5F5F5] transition-colors" data-id="element-402">
                  Decoder
                </Link>
                <Link to="/careers" className="hover:text-[#F5F5F5] transition-colors" data-id="element-403">
                  Careers
                </Link>
                <Link to="/learnmore/founder" className="hover:text-[#F5F5F5] transition-colors" data-id="element-404">
                  Founder
                </Link>
                <a href="https://wa.me/919633096997" target="_blank" rel="noopener noreferrer" className="hover:text-[#F5F5F5] transition-colors" data-id="element-405">
                  Contact
                </a>
              </div>

              <div className="text-xs text-gray-600 font-mono" data-id="element-406">
                © {new Date().getFullYear()}
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>;
}